import {
  Component,
  ContentChild,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { HintType } from '@shared/modules/hint/classes/HintType';
import { getStringId } from '@shared/utils/get-string-id.util';
import { Subject } from 'rxjs';
import { FormInputComponent } from '@shared/modules/form-components/base/form-input.component';

@UntilDestroy()
@Component({
  template: '',
})
export abstract class FormInputBaseComponent<Type = unknown, OnChangeType = Type>
  extends FormInputComponent
  implements ControlValueAccessor, OnChanges, OnDestroy {
  @ContentChild('customFieldLabel', { read: TemplateRef })
  customLabelTemplate: TemplateRef<unknown>;

  @Input() id: string;
  @Input() name: string;
  @Input() disabled: boolean;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() alwaysShowErrors = false;
  @Input() hintText: string;
  @Input() isHintShown = false;
  @Input() customError: { error: string; message: string };

  hintType = HintType;
  textFieldId = getStringId();
  changeSubject$ = new Subject<SimpleChanges>();

  onChange: (value: OnChangeType) => void;
  onTouched: () => void;
  value: Type;
  control: AbstractControl | FormControl;

  ngOnChanges(changes: SimpleChanges): void {
    this.changeSubject$.next(changes);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: Type): void {
    this.value = value;
  }

  getErrorMessage(): string {
    const errorPrefix = 'FORM_COMPONENTS.ERROR.';
    let errorSuffix = '';
    if (this.control.pristine && !this.alwaysShowErrors) {
      return errorSuffix;
    }
    if (this.customError) {
      errorSuffix = this.handleCustomError();
    } else if (this.control.hasError('required')) {
      errorSuffix = 'required';
    } else {
      errorSuffix = this.control.errors ? 'value-invalid' : '';
    }
    return errorSuffix ? errorPrefix + errorSuffix : errorSuffix;
  }

  private handleCustomError(): string {
    return this.control.hasError(this.customError.error) ? this.customError.message : '';
  }

  ngOnDestroy(skip?: boolean) {
    this.changeSubject$.complete();
    return super.ngOnDestroy(skip);
  }
}
